import React from 'react';
import { Toast } from 'primereact/toast';
import type { ToastMessage } from 'primereact/toast';

/** PrimeReact's ToastMessage severity type doesn't include 'help', but
 *  NetworkStatus.scss styles .p-toast-message-help, so we extend the type
 *  locally instead of casting to `any`. */
type AppToastSeverity = ToastMessage['severity'] | 'help';
type AppToastMessage = Omit<ToastMessage, 'severity'> & { severity?: AppToastSeverity };

/** Shared "icon + centered text + arrow button" body used by the
 *  delegate and approval action toasts. */
const buildActionToast = (
    icon: string,
    message: string,
    onClick: () => void,
    ariaLabel: string,
): React.ReactElement => (
    <div className="nw-action-toast">
        <i className={`pi ${icon} nw-action-icon`} />
        <div className="nw-action-text">{message}</div>
        <button
            type="button"
            className="pi pi-arrow-right nw-action-arrow-btn"
            aria-label={ariaLabel}
            onClick={onClick}
        />
    </div>
);

/**
 * Singleton service that holds the PrimeReact Toast ref.
 * Allows apiSlice (non-React context) to trigger network status toasts
 * without prop drilling or Redux.
 *
 * NOTE: methods rely on `this` referring to networkToastService. Always
 * call as `networkToastService.method()` — do not destructure methods off
 * this export, or the `this` binding is lost.
 */
const networkToastService = {
    toastRef: null as Toast | null,
    isOffline: false,
    isDelegateShown: false,
    isCorrectiveShown: false,

    offlineMsg: null as AppToastMessage | null,
    delegateMsg: null as AppToastMessage | null,
    correctiveMsg: null as AppToastMessage | null,
    approvalMsg: null as AppToastMessage | null,
    isApprovalShown: false,
    approvalOnClick: null as (() => void) | null,
    correctiveOnClick: null as (() => void) | null,
    onSharedToastsChange: null as (() => void) | null,

    /** Registers (or clears, on unmount) the Toast ref from NetworkStatus. */
    setRef(ref: Toast | null) {
        this.toastRef = ref;
    },

    /** Re-renders the toast stack from current message state. */
    refresh() {
        if (!this.toastRef) return;
        const messages = [
            this.offlineMsg,
            this.delegateMsg,
            this.correctiveMsg,
            this.approvalMsg,
        ].filter(Boolean) as ToastMessage[];
        this.toastRef.clear();
        if (messages.length > 0) {
            this.toastRef.show(messages);
        }
        this.onSharedToastsChange?.();
    },

    /** Shows the sticky offline error toast. No-op if already shown. */
    showOffline() {
        if (this.isOffline) return;
        this.isOffline = true;
        this.offlineMsg = {
            severity: 'error',
            sticky: true,
            closable: false,
            content: (
                <div className="nw-toast-content">
                    <i className="pi pi-wifi nw-toast-icon nw-icon-offline" />
                    <div className="nw-toast-body">
                        <span className="nw-toast-title">No Network Connection</span>
                        <span className="nw-toast-detail">
                            You are disconnected from the TTEC network or VPN.
                            Grids and data will not load until you reconnect.
                        </span>
                    </div>
                    <i className="pi pi-spin pi-spinner nw-toast-spinner" />
                </div>
            ),
        };
        this.refresh();
    },

    /** Clears the offline toast and shows a transient "restored" success toast. */
    showOnline() {
        if (!this.isOffline) return;
        this.isOffline = false;
        this.offlineMsg = {
            severity: 'success',
            sticky: false,
            life: 3000,
            closable: false,
            content: (
                <div className="nw-toast-content">
                    <i className="pi pi-wifi nw-toast-icon nw-icon-online" />
                    <div className="nw-toast-body">
                        <span className="nw-toast-title">Connection Restored</span>
                        <span className="nw-toast-detail">
                            You are back on the TTEC network. Your data is refreshing.
                        </span>
                    </div>
                </div>
            ),
        };
        this.refresh();
        // Success toast is transient (life: 3000) — clear from state right
        // after showing so a later refresh() doesn't resurrect it.
        this.offlineMsg = null;
    },

    /** Shows the delegation-request action toast. No-op if already shown. */
    showDelegate(onArrowClick: () => void) {
        if (this.isDelegateShown) return;
        this.isDelegateShown = true;
        this.delegateMsg = {
            severity: 'help',
            sticky: true,
            closable: true,
            content: buildActionToast(
                'pi-arrow-right-arrow-left',
                'You have a new delegation request',
                onArrowClick,
                'View delegation request',
            ),
        };
        this.refresh();
    },

    /** Hides the delegation-request toast, if shown. */
    hideDelegate() {
        if (!this.isDelegateShown) return;
        this.isDelegateShown = false;
        this.delegateMsg = null;
        this.refresh();
    },

    /**
     * Shows the corrective-action toast. Unlike showDelegate/showApproval,
     * this intentionally does NOT guard on isCorrectiveShown — a newer
     * corrective action's message/callback should replace an older one
     * even while a toast is still visible.
     */
    showCorrective(message: string, onArrowClick: () => void) {
        this.correctiveOnClick = onArrowClick;
        this.isCorrectiveShown = true;
        this.correctiveMsg = {
            sticky: true,
            closable: true,
            className: 'custon-pink',
            content: (
                <div className="nw-corrective-toast">
                    <i className="pi pi-history nw-corrective-icon" />
                    <span className="nw-corrective-text">{message}</span>
                    <button
                        type="button"
                        className="pi pi-arrow-right nw-corrective-arrow-btn"
                        aria-label="View corrective action"
                        onClick={() => {
                            networkToastService.correctiveOnClick?.();
                            networkToastService.isCorrectiveShown = false;
                            networkToastService.correctiveMsg = null;
                            networkToastService.correctiveOnClick = null;
                            networkToastService.refresh();
                        }}
                    />
                </div>
            ),
        };
        this.refresh();
    },

    /** Hides the corrective-action toast, if shown. */
    hideCorrective() {
        if (!this.isCorrectiveShown) return;
        this.isCorrectiveShown = false;
        this.correctiveMsg = null;
        this.correctiveOnClick = null;
        this.refresh();
    },

    /** Shows the approval-request action toast. No-op if already shown. */
    showApproval(message: string, onArrowClick: () => void) {
        this.approvalOnClick = onArrowClick;
        if (this.isApprovalShown) return;
        this.isApprovalShown = true;
        this.approvalMsg = {
            severity: 'help',
            sticky: true,
            closable: true,
            className: 'approval-toast',
            content: buildActionToast(
                'pi-exclamation-circle',
                message,
                () => networkToastService.approvalOnClick?.(),
                'View approval request',
            ),
        };
        this.refresh();
    },

    /** Hides the approval-request toast, if shown. */
    hideApproval() {
        if (!this.isApprovalShown) return;
        this.isApprovalShown = false;
        this.approvalMsg = null;
        this.approvalOnClick = null;
        this.refresh();
    },

    get sharedToastCount() {
        return [this.offlineMsg, this.delegateMsg, this.correctiveMsg, this.approvalMsg].filter(Boolean).length;
    },
};

export default networkToastService;
